/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { ChangeEvent, FC, useEffect, useState } from "react"
import { Form } from "grab-ui"
import { Drawer, Row, Col, Input, List, Checkbox, Button } from "@duxton-web/ui"
import { ChevronLeftUniversal, SearchUniversal } from "@duxton-web/icons"
import { Item } from "types/models/menu"
import { OverflowTip } from "components/common"
import LoadMore from "components/common/LoadMore"
import { useTranslation } from "common/utils/i18n"
import useMenuContext from "hooks/useMenuContext"
import { LinkedItemsDrawerLoading } from "./LinkedItemsDrawerLoading"
import {
  drawerFooter,
  LinkItemsDrawerStyles,
  listItemWrapper,
} from "./ModifierGroups.styles"

const SEARCH_FIELD = "search"

interface props extends FormComponentProps {
  visible: boolean
  modifierGroupID: string
  onCloseDrawer: () => void
  onItemCheckboxChange: (itemID?: string) => void
  onSubmitLinkitem: () => void
  onSearchItem: (e: ChangeEvent<HTMLInputElement>) => void
  items: Item[]
  linkedItems: Item[]
  isLoading?: boolean
  hasNextPage?: boolean
  onLoadMore?: () => void
}

export const LinkedItemsDrawerComponent: FC<props> = ({
  visible,
  onCloseDrawer,
  onItemCheckboxChange,
  onSubmitLinkitem,
  onSearchItem,
  items,
  linkedItems,
  modifierGroupID,
  isLoading,
  hasNextPage,
  onLoadMore,
  form,
}: props) => {
  const { getFieldDecorator, resetFields } = form
  const [changesIsMade, setChangesIsMade] = useState<boolean>(false)
  const { isServiceBasedMenuEnabled } = useMenuContext()

  useEffect(() => {
    if (!visible) {
      setChangesIsMade(false)
      resetFields()
    }
  }, [visible, resetFields])

  // Render load more component
  // Note: hasNextPage and onLoadMore are set together in parent component,
  // so if hasNextPage is truthy, onLoadMore should be available
  const renderLoadMore = () => {
    if (hasNextPage && onLoadMore) {
      return (
        <LoadMore
          onLoadMore={onLoadMore}
          component={<LinkedItemsDrawerLoading />}
        />
      )
    }
    return null
  }

  const linkedItemsCount = linkedItems.length
  return (
    <Drawer
      destroyOnClose
      title={useTranslation("menu.modifierGroup.button.linkItem")}
      open={visible}
      onClose={onCloseDrawer}
      css={LinkItemsDrawerStyles}
      width={450}
      closeIcon={<ChevronLeftUniversal />}
    >
      <div
        css={css`
          padding: 24px;
        `}
      >
        <Row>
          <Col span={24}>
            {getFieldDecorator(SEARCH_FIELD)(
              <Input
                prefix={<SearchUniversal />}
                placeholder={useTranslation(
                  "menu.modifierGroup.placeholder.searchByItemName",
                )}
                onChange={e => onSearchItem(e)}
              />,
            )}
          </Col>
        </Row>
        <Row css={listItemWrapper}>
          <Col span={24}>
            <List
              data-test="item-list"
              itemLayout="horizontal"
              loading={isLoading}
              loadMore={renderLoadMore()}
              locale={{
                emptyText: useTranslation(
                  "menu.item.modifierGroup.noDataAvailable",
                ),
              }}
              dataSource={items.map((item, index) => ({
                ...item,
                sectionName:
                  items[index - 1]?.sectionID === item.sectionID
                    ? ""
                    : item.sectionName,
              }))}
              renderItem={item => (
                <List.Item
                  style={{ cursor: "pointer" }}
                  onClick={() => {
                    onItemCheckboxChange(item.itemID)
                    setChangesIsMade(true)
                  }}
                >
                  <div className="sectionContent">
                    <OverflowTip>{item?.sectionName}</OverflowTip>
                  </div>
                  <List.Item.Meta
                    avatar={
                      <Checkbox
                        checked={
                          !!linkedItems.find(i => i.itemID === item.itemID)
                        }
                      />
                    }
                    description={
                      <div>
                        <div className="listItemWrapper">
                          <OverflowTip>{item.itemName}</OverflowTip>
                          <span>
                            {isServiceBasedMenuEnabled
                              ? item.priceRange
                              : item.priceDisplay}
                          </span>
                        </div>
                        <div>
                          <OverflowTip>{item?.categoryName}</OverflowTip>
                        </div>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Col>
        </Row>
      </div>
      <div css={drawerFooter}>
        <Button
          type="primary"
          block
          disabled={(!linkedItemsCount && !modifierGroupID) || !changesIsMade}
          onClick={onSubmitLinkitem}
        >
          {useTranslation("menu.modifierGroup.button.linkXItem").replace(
            /<x>/i,
            `${linkedItemsCount}`,
          )}
        </Button>
      </div>
    </Drawer>
  )
}

export default Form.create<props>({})(LinkedItemsDrawerComponent)
